import { act, screen } from '@testing-library/react';
import { createRef } from 'react';
import { beforeAll, afterAll, describe, it, expect } from 'vitest';
import '@testing-library/jest-dom/vitest';

import { AiResponse } from '../AiResponse.js';
import {
  render,
  setupMatchMediaMock,
  clearMatchMediaMock,
} from './vite-helpers.js';

const imageSrc =
  'data:image/jpeg;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+P+/HgAFhAJ/wlseKgAAAABJRU5ErkJggg==';

describe('AiResponse reduced motion', () => {
  beforeAll(() => {
    // simulate reduced motion preference - these tests solely should check if the response is rendered correctly
    // we keep these tests in a separate file since framer motion caches the matchMedia result and changing it within the same test file is not possible
    setupMatchMediaMock(true);
  });

  afterAll(() => {
    clearMatchMediaMock();
  });

  it('should not render anything if displayedText is an empty string', async () => {
    // given
    const response = '';

    // when
    render(<AiResponse data-testid="ai-response">{response}</AiResponse>);

    // then
    expect(screen.queryByTestId('ai-response')).toBeNull();
  });

  describe('markdown rendering', () => {
    it('should render headings correctly', async () => {
      // given
      const response = `
# How to Optimize Your Workflow

## Introduction
In today's fast-paced environment, efficiency is key. Here are some strategies to help you optimize your workflow:

### 1. Automate Repetitive Tasks
Use tools and scripts to automate tasks you perform frequently. For example:
`;
      const headings = [
        'How to Optimize Your Workflow',
        'Introduction',
        '1. Automate Repetitive Tasks',
      ];

      // when
      await act(async () => {
        render(<AiResponse>{response}</AiResponse>);
      });

      // then
      headings.forEach((text, i) => {
        const headingElement = screen.getByText(text);
        expect(headingElement).toBeInTheDocument();
        expect(headingElement.getAttribute('data-testid')).toBe('heading');
        expect(headingElement.tagName).toBe(`H${i + 1}`);
      });
    });

    it('should render link correctly', async () => {
      // given
      const response = '[Link text](https://example.com)';

      // when
      await act(async () => {
        render(<AiResponse>{response}</AiResponse>);
      });

      // then
      const link = screen.getByText('Link text');
      expect(link.tagName).toBe('A');
      expect(link).toHaveAttribute('href', 'https://example.com/');
    });

    it('should render code block correctly', async () => {
      // given
      const response = `
\`\`\`js
const greet = name => \`Hello, \${name}!\`;
console.log(greet('World'));
\`\`\`
`;

      // when
      await act(async () => {
        render(<AiResponse>{response}</AiResponse>);
      });

      // then
      const codeblock = screen.getByTestId('code-snippet');
      expect(
        codeblock.textContent?.endsWith("console.log(greet('World'));"),
      ).toBeTruthy();
    });

    it('should render list items correctly', async () => {
      // given
      const response = `
* Urgent & Important
* Important but Not Urgent
* Urgent but Not Important
* Neither
`;

      // when
      await act(async () => {
        render(<AiResponse>{response}</AiResponse>);
      });

      // then
      const items = screen.getAllByRole('listitem');
      expect(items.map((item) => item.textContent)).toStrictEqual([
        'Urgent & Important',
        'Important but Not Urgent',
        'Urgent but Not Important',
        'Neither',
      ]);
    });

    it('should render plain text correctly', async () => {
      // given
      const response = 'Regular breaks improve focus and productivity.';

      // when
      await act(async () => {
        render(<AiResponse>{response}</AiResponse>);
      });

      // then
      const plainText = screen.getByText(
        'Regular breaks improve focus and productivity.',
      );
      expect(plainText.tagName).toBe('P');
    });

    it('should render bold text correctly', async () => {
      // given
      const response =
        '**Tip: Try the Pomodoro Technique for time management.**';

      // when
      await act(async () => {
        render(<AiResponse>{response}</AiResponse>);
      });

      // then
      const bold = screen.getByText(
        'Tip: Try the Pomodoro Technique for time management.',
      );
      expect(bold.tagName).toBe('STRONG');
    });

    it('should render image correctly', async () => {
      // given
      const response = `![Workflow Diagram](${imageSrc})`;

      // when
      await act(async () => {
        render(<AiResponse>{response}</AiResponse>);
      });

      // then
      // Streamdown blocks images by default and renders a placeholder
      const placeholder = screen.getByText('[Image blocked: Workflow Diagram]');
      expect(placeholder).toBeInTheDocument();
    });
  });

  describe('a11y', () => {
    it('should have correct aria attributes by default', async () => {
      // given / when
      await act(async () =>
        render(
          <AiResponse data-testid="ai-response">
            Some AI-generated response.
          </AiResponse>,
        ),
      );

      // then
      const root = screen.getByTestId('ai-response');
      expect(root).toHaveAttribute('aria-live', 'polite');
      expect(root).toHaveAttribute('aria-atomic', 'true');
    });

    it('should support other aria attributes', async () => {
      // given/when
      await act(async () =>
        render(
          <section aria-busy="true" aria-label="AI Response Section">
            <div id="response-label">AI Response</div>
            <div id="description">AI-generated content generating...</div>
            <div id="details">This may take a few seconds.</div>
            <AiResponse
              data-testid="ai-response"
              aria-label="label"
              aria-labelledby="response-label"
              aria-describedby="description"
              aria-details="details"
            >
              This is an AI-generated answer about your request.
            </AiResponse>
          </section>,
        ),
      );

      // then
      const response = screen.getByTestId('ai-response');
      expect(response).toHaveAttribute('aria-label', 'label');
      expect(response).toHaveAttribute('aria-labelledby', 'response-label');
      expect(response).toHaveAttribute('aria-describedby', 'description');
      expect(response).toHaveAttribute('aria-details', 'details');
    });
  });

  it('should forward the ref', async () => {
    // given
    const forwardedRef = createRef<HTMLDivElement>();

    // when
    await act(async () =>
      render(
        <AiResponse data-testid="ai-response" ref={forwardedRef}>
          Some AI-generated response.
        </AiResponse>,
      ),
    );

    // then
    expect(forwardedRef.current).not.toBeNull();
    expect(forwardedRef.current?.tagName).toBe('DIV');
  });

  it('should set data-dtrum on the root level of the component', async () => {
    // given/when
    await act(async () =>
      render(
        <AiResponse data-dtrum-mask data-dtrum-allow data-testid="dtrum">
          Some AI-generated response.
        </AiResponse>,
      ),
    );

    // then
    const dtrum = screen.getByTestId('dtrum');
    expect(dtrum).toHaveAttribute('data-dtrum-mask');
    expect(dtrum).toHaveAttribute('data-dtrum-allow');
  });

  it('should apply consumer className and style', async () => {
    // given/when
    await act(async () =>
      render(
        <AiResponse
          data-testid="ai-response"
          className="ai-response-class"
          style={{ backgroundColor: 'rgb(255, 182, 193)' }}
        >
          Some AI-generated response.
        </AiResponse>,
      ),
    );

    // then
    expect(screen.getByTestId('ai-response')).toHaveClass('ai-response-class');
    expect(screen.getByTestId('ai-response')).toHaveStyle(
      'background-color: rgb(255, 182, 193)',
    );
  });

  it('should spread remaining props', async () => {
    // given/when
    await act(async () =>
      render(
        <AiResponse data-testid="ai-response" data-custom="test-custom-data">
          Some AI-generated response.
        </AiResponse>,
      ),
    );

    // then
    expect(screen.getByTestId('ai-response')).toHaveAttribute(
      'data-custom',
      'test-custom-data',
    );
  });

  describe('behavior tracking attributes', () => {
    it('should add data-dt-* attributes', () => {
      render(
        <AiResponse
          data-testid="ai-response"
          data-dt-content="AiContent"
          data-dt-value-content="ResponseValue"
          data-dt-children-value-content="MessageContent"
        >
          This is an AI-generated answer about your request.
        </AiResponse>,
      );

      const aiResponse = screen.getByTestId('ai-response');
      expect(aiResponse).toHaveAttribute('data-dt-content', 'AiContent');
      expect(aiResponse).toHaveAttribute(
        'data-dt-value-content',
        'ResponseValue',
      );
      expect(aiResponse).toHaveAttribute(
        'data-dt-children-value-content',
        'MessageContent',
      );
    });

    describe('data-dt-component', () => {
      it('should have the default data-dt-component attribute', () => {
        render(
          <AiResponse data-testid="ai-response">
            This is an AI-generated answer about your request.
          </AiResponse>,
        );
        const aiResponse = screen.getByTestId('ai-response');
        expect(aiResponse).toHaveAttribute('data-dt-component', 'AiResponse');
      });

      it('should not override the data-dt-component attribute if provided', () => {
        render(
          <AiResponse
            data-testid="ai-response"
            data-dt-component="CustomAiResponse"
          >
            This is an AI-generated answer about your request.
          </AiResponse>,
        );
        const aiResponse = screen.getByTestId('ai-response');
        expect(aiResponse).toHaveAttribute('data-dt-component', 'AiResponse');
      });
    });
  });
});
